import { InformationCircleIcon, MinusIcon, PlusIcon } from "@heroicons/react/24/outline";
import { useI18N } from "@sundae/react-hooks";
import {
  Balance,
  FlexList,
  FlexListItem,
  Loader,
  Text,
  Tooltip,
  toolkitCx,
} from "@sundae/ui-toolkit";
import cx from "classnames";
import { useEffect, useMemo } from "react";

import { Status } from "../../../gql/generated/taste-test.sdk";
import { useCurrentTasteTestContribution } from "../../../hooks/taste-test/useCurrentTasteTestContribution";
import { useTasteTestWithdrawAmounts } from "../../../hooks/taste-test/useTasteTestWithdrawAmounts";
import { useTasteTestWithdrawOrderFees } from "../../../hooks/taste-test/useTasteTestWithdrawOrderFees";
import { useTasteTestContext } from "../../../pages/TasteTestDetail/context";
import { useTasteTestTx } from "../../../pages/TasteTestDetail/hooks/useTasteTestTx";
import { EOrderTransactionBuilderState, useOrderContext } from "../../../stores/OrderContext";
import { useProfileAppContext } from "../../../stores";

const TasteTestWithdrawDetails = () => {
  const { t, currentLocale } = useI18N("taste-test");
  const {
    state: {
      derived: { transaction },
    },
  } = useOrderContext();
  const {
    state: { tasteTest },
  } = useTasteTestContext();
  const {
    state: { privacyEnabled },
  } = useProfileAppContext();
  const { adaTxFee, penaltyFee } = useTasteTestWithdrawOrderFees();
  const { foldFee, refundableDeposit } = useTasteTestWithdrawAmounts();
  const currentContribution = useCurrentTasteTestContribution();
  const { buildTx } = useTasteTestTx({ type: "withdraw" });

  /* -------------------------------------------------------------------------------------------------
   * Transaction Fees (ADA Tx Fee, Penalty Fee)
   * -----------------------------------------------------------------------------------------------*/
  const txFees = useMemo(() => {
    const penaltyPercentage = tasteTest?.parameters.penaltyPercentage ?? 0;

    return [
      {
        id: "1",
        label: t("fee", { context: "adaTx", ns: "fees" }),
        tooltip: t("fee", { context: "adaTxExplained", ns: "fees" }),
        value: (
          <div className="inline-flex items-center gap-[2px]">
            <div className="flex items-center gap-2">
              <MinusIcon
                className={cx("absolute -left-5 h-3 w-3 opacity-75", toolkitCx.layout.text.error)}
              />
              {transaction?.state === EOrderTransactionBuilderState.BUILD ? (
                <Loader className="mr-2" size="xsmall" />
              ) : (
                <Balance
                  abbreviate={false}
                  locale={currentLocale}
                  showSymbol
                  size="xl"
                  value={adaTxFee}
                  variant="normal"
                  weight="medium"
                />
              )}
            </div>
          </div>
        ),
      },
      ...(tasteTest?.status === Status.LastCall
        ? [
            {
              id: "2",
              label: t("detail.txOverview.penalty"),
              tooltip: t("detail.txOverview.penalty", {
                context: "tooltip",
                penaltyPercentage: penaltyPercentage * 100,
              }),
              value: (
                <div className="inline-flex items-center gap-[2px]">
                  <div className="flex items-center gap-2">
                    <MinusIcon
                      className={cx(
                        "absolute -left-5 h-3 w-3 opacity-75",
                        toolkitCx.layout.text.error
                      )}
                    />
                    {transaction?.state === EOrderTransactionBuilderState.BUILD ? (
                      <Loader className="mr-2" size="xsmall" />
                    ) : (
                      <Balance
                        abbreviate={false}
                        locale={currentLocale}
                        showSymbol
                        size="xl"
                        value={penaltyFee}
                        variant="normal"
                        weight="medium"
                      />
                    )}
                  </div>
                </div>
              ),
            },
          ]
        : []),
    ];
  }, [adaTxFee, currentLocale, tasteTest?.parameters.penaltyPercentage, transaction?.state]);

  /* -------------------------------------------------------------------------------------------------
   * Transaction Values (Current Contribution, Refundable Deposit, Fold Fee)
   * -----------------------------------------------------------------------------------------------*/
  const txValues = useMemo(() => {
    return [
      {
        id: 0,
        value: currentContribution,
        label: t("detail.widget.currentContribution"),
      },
      {
        id: 1,
        value: refundableDeposit,
        label: t("detail.widget.refundableDeposit"),
        tooltip: t("detail.widget.refundableDeposit", { context: "tooltip" }),
      },
      {
        id: 2,
        value: foldFee,
        label: t("detail.widget.processorFee"),
        tooltip: t("detail.widget.processorFee", { context: "tooltip" }),
      },
    ];
  }, [currentContribution, refundableDeposit, foldFee]);

  /* -------------------------------------------------------------------------------------------------
   * Build Transaction
   * -----------------------------------------------------------------------------------------------*/
  useEffect(() => {
    buildTx();
  }, []);

  if (!tasteTest) return null;

  return (
    <div className="grid gap-2">
      {txValues.map(({ id, value, label, tooltip }, index) => (
        <div key={`${id}-${index}`} className="relative flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PlusIcon
              className={cx("absolute -left-5 h-3 w-3 opacity-75", toolkitCx.layout.text.success)}
            />
            <Text className="flex items-center gap-1" tag="span" size="sm" variant="muted">
              {label}
              {tooltip && (
                <Tooltip
                  tooltipLabel={tooltip}
                  trigger={
                    <div>
                      <InformationCircleIcon
                        className={cx("h-4 w-4 cursor-help", toolkitCx.layout.text.muted)}
                      />
                    </div>
                  }
                />
              )}
            </Text>
          </div>
          <Balance
            abbreviate={false}
            locale={currentLocale}
            privacy={privacyEnabled}
            size="xl"
            value={value}
            weight="medium"
          />
        </div>
      ))}

      <FlexList>
        {txFees.map(({ id, label, value, tooltip }) => (
          <div className="relative flex w-full items-center gap-2" key={id}>
            <FlexListItem
              className="relative min-h-[32px] w-full justify-between"
              tooltip={tooltip}
              field={{
                label,
                variant: "muted",
                size: "sm",
              }}
              value={{
                label: value,
              }}
            />
          </div>
        ))}
      </FlexList>
    </div>
  );
};

export default TasteTestWithdrawDetails;
